import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ApplicationLayout from './ApplicationLayout';
import ApplicationLayoutPublic from './ApplicationLayoutPublic';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/apiClient';
import { buildContentModel, extractAssetUrls, setTokenCache, resolveContentfulResponse } from '../../utils/contentfulUtils';
import assetTokenService from '../../services/assetTokenService';
import {
  currentUserContext,
  localeContext,
  appCopyContext,
  hubAppCopyContext,
} from '../../contexts';
import { HubContentSkeleton } from '../ui/skeletons';

// Standard layout wrapper for authenticated pages
export const MainLayoutWrapper = ({ children }) => {
  const { 
    user: currentUser, 
    locale, 
    appCopy, 
    hubAppCopy, 
    programs, 
    userPrograms,
    userApps, 
    currentHubToolSlug: activeHubSlug,
    // Add any other props that ApplicationLayout needs
  } = useApp();
  
  const { isMasquerading } = useAuth();

  return (
    <ApplicationLayout
      currentUser={currentUser}
      appCopy={appCopy}
      hubAppCopy={hubAppCopy}
      locale={locale}
      programs={programs}
      userPrograms={userPrograms}
      userApps={userApps}
      activeHubSlug={activeHubSlug}
      masqueradedUser={isMasquerading()}
      // Add other required props as needed
    >
      {children}
    </ApplicationLayout>
  );
};

// Layout wrapper for Programs pages (shows program list in sidebar)
export const ProgramsLayoutWrapper = ({ children }) => {
  const { hubtoolslug } = useParams();
  const activeHubSlug = hubtoolslug || 'knowledge';
  const { 
    user: currentUser, 
    locale, 
    appCopy, 
    hubAppCopy, 
    userPrograms,
    userApps
  } = useApp();
  
  const { isMasquerading } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        const response = await api.getPrograms(activeHubSlug, locale || 'en-US');

        // API returns: response.data = { data: { items: [...], total: N }, status: 'success' }
        // Extract the actual programs data from the nested structure
        const programsData = response.data?.data;

        // Transform the items directly from the API response
        // Direct HTTP returns: { sys: {...}, fields: {...} } for each item
        // We extract sys.id, sys.locale, and flatten the fields
        const resolvedPrograms = programsData?.items?.map(item => {
          // Direct HTTP returns fields directly, prosci_contentful gem used raw.fields
          const fields = item.fields || item.raw?.fields || {};
          return {
            id: item.sys?.id,
            locale: item.sys?.locale || 'en-US',
            ...fields
          };
        }) || [];

        setPrograms(resolvedPrograms)
      } catch (error) {
        console.error('Failed to fetch programs for layout:', error);
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
    // Re-fetch when the active workspace changes too — hub program access is
    // workspace-scoped server-side, but nothing else in this effect's deps
    // changes when the user switches workspaces without navigating away.
  }, [activeHubSlug, locale, currentUser?.activeWorkspaceId]);
  
  return (
    <ApplicationLayout
      currentUser={currentUser}
      appCopy={appCopy}
      hubAppCopy={hubAppCopy}
      locale={locale}
      programs={programs}
      userPrograms={userPrograms}
      userApps={userApps}
      activeHubSlug={activeHubSlug}
      masqueradedUser={isMasquerading()}
    >
      {loading ? <HubContentSkeleton /> : children}
    </ApplicationLayout>
  );
};

// Layout wrapper for Program-specific pages (shows program navigation in sidebar)  
export const ProgramLayoutWrapper = ({ children }) => {
  const { slug, hubtoolslug } = useParams();
  const activeHubSlug = hubtoolslug || 'knowledge';
  const { 
    user: currentUser, 
    locale, 
    appCopy, 
    hubAppCopy, 
    userPrograms,
    userApps
  } = useApp();
  
  const { isMasquerading } = useAuth();
  const [program, setProgram] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProgramData = async () => {
      try {
        setLoading(true);
        
        // Fetch both the specific program and all programs for the hub
        const [programResponse, programsResponse] = await Promise.all([
          api.getProgram(slug, locale || 'en-US'),
          api.getPrograms(activeHubSlug, locale || 'en-US')
        ]);
        // Handle raw Contentful response format for program
        const rawProgramData = programResponse.data?.data || programResponse.data;
        let programData: any = rawProgramData;

        if (rawProgramData?.items) {
          // Full Contentful response format
          programData = resolveContentfulResponse(rawProgramData)[0];
        }
        
        // Handle raw Contentful response format for programs  
        let programsData = programsResponse.data?.data || programsResponse.data || programsResponse || [];
        if (Array.isArray(programsData)) {
          // Already in array format, likely processed
        } else {
          // If it's a single programs response, use as is
          programsData = programsData || [];
        }
        
        if (programData) {
          // Build the program model - URLs are rewritten at render time via rewriteUrl
          const builtProgram = buildContentModel(programData);

          // Add navTitle to level1ProgramUnits for sidebar navigation
          if (builtProgram?.level1ProgramUnits) {
            builtProgram.level1ProgramUnits = builtProgram.level1ProgramUnits.map((unit) => {
              const processedUnit = {
                ...unit,
                navTitle: unit.title_prefix ? `${unit.title_prefix} - ${unit.title}` : unit.title,
              };

              // Process nested units
              if (unit.level2ProgramUnits && Array.isArray(unit.level2ProgramUnits)) {
                processedUnit.level2ProgramUnits = unit.level2ProgramUnits.map((level2Unit) => {
                  const level2Processed = {
                    ...level2Unit,
                    navTitle: level2Unit.title,
                  };

                  if (level2Unit.level3ProgramUnits && Array.isArray(level2Unit.level3ProgramUnits)) {
                    level2Processed.level3ProgramUnits = level2Unit.level3ProgramUnits.map((level3Unit) => ({
                      ...level3Unit,
                      navTitle: level3Unit.title,
                    }));
                  }

                  return level2Processed;
                });
              }

              return processedUnit;
            });
          }

          setProgram(builtProgram);
        }
        
        setPrograms(programsData);
      } catch (error) {
        console.error('Failed to fetch program data for layout:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchProgramData();
    }
    // currentUser?.activeWorkspaceId: re-fetch on workspace switch, same
    // reasoning as ProgramsLayoutWrapper above.
  }, [slug, activeHubSlug, locale, currentUser?.id, currentUser?.activeWorkspaceId]);
  
  return (
    <ApplicationLayout
      currentUser={currentUser}
      appCopy={appCopy}
      hubAppCopy={hubAppCopy}
      locale={locale}
      programs={programs}
      program={program}
      userPrograms={userPrograms}
      userApps={userApps}
      activeHubSlug={activeHubSlug}
      masqueradedUser={isMasquerading()}
    >
      {loading ? <HubContentSkeleton /> : children}
    </ApplicationLayout>
  );
};

// Dashboard layout wrapper - uses its own sidebar, not NavigationLayout
export const DashboardLayoutWrapper = ({ children }) => {
  const { user: currentUser, locale, appCopy, hubAppCopy } = useApp();
  const { isMasquerading, stopMasquerading } = useAuth();

  // Resolve the copy objects if they have sys property (raw Contentful format)
  const resolvedAppCopy = appCopy?.sys ? resolveContentfulResponse(appCopy)[0] : appCopy;
  const resolvedHubAppCopy = hubAppCopy?.sys ? resolveContentfulResponse(hubAppCopy)[0] : hubAppCopy;

  // Dashboard handles its own layout with sidebar, but we need to provide copy contexts
  return (
    <localeContext.Provider value={locale}>
      <hubAppCopyContext.Provider value={resolvedHubAppCopy}>
        <appCopyContext.Provider value={resolvedAppCopy}>
          <currentUserContext.Provider value={currentUser}>
            {/* Masquerade Banner - Sticky footer warning when admin is impersonating a user */}
            {isMasquerading() && (
              <div
                style={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2147483647, // Max z-index to ensure it's above chat widgets
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                  pointerEvents: 'auto',
                }}
              >
                <span>
                  <strong>Masquerading as:</strong> {currentUser?.email}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    stopMasquerading();
                  }}
                  style={{
                    backgroundColor: '#1f2937',
                    color: 'white',
                    padding: '6px 16px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    pointerEvents: 'auto',
                  }}
                >
                  Stop Masquerading
                </button>
              </div>
            )}
            {children}
          </currentUserContext.Provider>
        </appCopyContext.Provider>
      </hubAppCopyContext.Provider>
    </localeContext.Provider>
  );
};

// Public layout wrapper for unauthenticated pages
export const PublicLayoutWrapper = ({ children }) => {
  const { 
    user: currentUser, 
    locale, 
    appCopy, 
    hubAppCopy,
    userApps,
  } = useApp();

  return (
    <ApplicationLayoutPublic
      currentUser={currentUser}
      appCopy={appCopy}
      hubAppCopy={hubAppCopy}
      locale={locale}
      userApps={userApps}
    >
      {children}
    </ApplicationLayoutPublic>
  );
};

export default MainLayoutWrapper;